// Minimal arithmetic evaluator for the numeric formula fields (+ - * / and
// parentheses over decimal numbers). Recursive descent over a token list —
// never eval/Function, so a typed expression can't execute anything.

type Token = { type: "number"; value: number } | { type: "op"; value: string };

const tokenize = (input: string): Token[] | null => {
    const tokens: Token[] = [];
    let i = 0;

    while (i < input.length) {
        const char = input[i];

        if (char === " " || char === "\t") {
            i++;
            continue;
        }

        if (char >= "0" && char <= "9" || char === ".") {
            let raw = "";
            while (i < input.length && (input[i] >= "0" && input[i] <= "9" || input[i] === ".")) {
                raw += input[i];
                i++;
            }
            const value = Number(raw);
            if (!Number.isFinite(value)) return null;
            tokens.push({ type: "number", value });
            continue;
        }

        if ("+-*/()".includes(char)) {
            tokens.push({ type: "op", value: char });
            i++;
            continue;
        }

        return null;
    }

    return tokens;
}

class Parser {
    private position = 0;

    constructor(private readonly tokens: Token[]) { }

    private peek(): Token | undefined {
        return this.tokens[this.position];
    }

    private eatOperator(...operators: string[]): string | null {
        const token = this.peek();
        if (token && token.type === "op" && operators.includes(token.value)) {
            this.position++;
            return token.value;
        }
        return null;
    }

    atEnd(): boolean {
        return this.position >= this.tokens.length;
    }

    // expression := term (("+" | "-") term)*
    expression(): number | null {
        let left = this.term();
        if (left === null) return null;

        for (; ;) {
            const operator = this.eatOperator("+", "-");
            if (!operator) return left;
            const right = this.term();
            if (right === null) return null;
            left = operator === "+" ? left + right : left - right;
        }
    }

    // term := factor (("*" | "/") factor)*
    private term(): number | null {
        let left = this.factor();
        if (left === null) return null;

        for (; ;) {
            const operator = this.eatOperator("*", "/");
            if (!operator) return left;
            const right = this.factor();
            if (right === null) return null;
            if (operator === "/" && right === 0) return null;
            left = operator === "*" ? left * right : left / right;
        }
    }

    // factor := ("+" | "-") factor | "(" expression ")" | number
    private factor(): number | null {
        const sign = this.eatOperator("+", "-");
        if (sign) {
            const value = this.factor();
            if (value === null) return null;
            return sign === "-" ? -value : value;
        }

        if (this.eatOperator("(")) {
            const value = this.expression();
            if (value === null) return null;
            if (!this.eatOperator(")")) return null;
            return value;
        }

        const token = this.peek();
        if (token && token.type === "number") {
            this.position++;
            return token.value;
        }
        return null;
    }
}

// Returns null when the expression is empty, malformed or does not produce a
// finite number, so callers can simply skip the update.
export const evaluateArithmetic = (expression: string): number | null => {
    const tokens = tokenize(expression);
    if (tokens === null || tokens.length === 0) return null;

    const parser = new Parser(tokens);
    const result = parser.expression();
    if (result === null || !parser.atEnd()) return null;
    return Number.isFinite(result) ? result : null;
}
